import { useState, useMemo } from 'react';
import { Smile, Search, Copy, Check, Clock, Heart, Zap, Globe, Coffee, Music, Star, Sun, Moon, Cloud, Flame, ThumbsUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const EMOJI_CATEGORIES = {
    'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕'],
    'Gestures': ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅'],
    'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️', '💌', '💋', '😻', '😽', '🫶', '🥰', '😍', '🤩'],
    'Animals': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🪲', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞'],
    'Food': ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕'],
    'Objects': ['⌚', '📱', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '💾', '💿', '📀', '🎥', '📷', '📸', '📹', '📼', '🔍', '🔎', '💡', '🔦', '🏮', '📔', '📕', '📖', '📗', '📘', '📙', '📓', '📃', '📄', '📰', '🗞️', '📑', '🔖', '💰', '💴', '💵', '💶', '💷', '💸', '💳', '🧾', '💹', '✉️', '📧', '📨', '📩', '📤', '📥', '📦', '📫'],
    'Symbols': ['❤️', '💔', '💯', '✨', '⭐', '🌟', '💫', '⚡', '🔥', '💥', '💢', '💦', '💨', '💤', '🕳️', '💣', '💬', '👁️‍🗨️', '🗨️', '🗯️', '💭', '💮', '♨️', '💈', '🛑', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛', '🌀', '♠️', '♥️', '♦️', '♣️', '🃏', '🀄', '🎴', '🔇', '🔈', '🔉', '🔊', '📢'],
    'Flags': ['🏁', '🚩', '🎌', '🏴', '🏳️', '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️', '🇦🇫', '🇦🇱', '🇩🇿', '🇦🇸', '🇦🇩', '🇦🇴', '🇦🇮', '🇦🇶', '🇦🇬', '🇦🇷', '🇦🇲', '🇦🇼', '🇦🇺', '🇦🇹', '🇦🇿', '🇧🇸', '🇧🇭', '🇧🇩', '🇧🇧', '🇧🇾', '🇧🇪', '🇧🇿', '🇧🇯', '🇧🇲', '🇧🇹', '🇧🇴', '🇧🇦', '🇧🇼', '🇧🇷', '🇮🇴', '🇻🇬', '🇧🇳', '🇧🇬', '🇧🇫', '🇧🇮', '🇰🇭', '🇨🇲', '🇨🇦', '🇮🇨', '🇨🇻', '🇧🇶', '🇰🇾'],
};

const CATEGORY_ICONS: { [key: string]: React.ElementType } = {
    'Smileys': Smile,
    'Gestures': ThumbsUp,
    'Hearts': Heart,
    'Animals': Star,
    'Food': Coffee,
    'Objects': Zap,
    'Symbols': Sun,
    'Flags': Globe,
};

const EmojiPicker = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Smileys');
    const [recentEmojis, setRecentEmojis] = useState<string[]>([]);
    const [copied, setCopied] = useState<string | null>(null);

    const allEmojis = useMemo(() => {
        return Object.values(EMOJI_CATEGORIES).flat();
    }, []);

    const filteredEmojis = useMemo(() => {
        if (!searchQuery.trim()) {
            return EMOJI_CATEGORIES[selectedCategory as keyof typeof EMOJI_CATEGORIES] || [];
        }
        // Simple search through all emojis
        return allEmojis;
    }, [searchQuery, selectedCategory, allEmojis]);

    const copyEmoji = (emoji: string) => {
        navigator.clipboard.writeText(emoji);
        setCopied(emoji);
        toast.success(`${emoji} copied!`);

        // Add to recent
        setRecentEmojis(prev => {
            const filtered = prev.filter(e => e !== emoji);
            return [emoji, ...filtered].slice(0, 20);
        });

        setTimeout(() => setCopied(null), 1000);
    };

    const categories = Object.keys(EMOJI_CATEGORIES);

    return (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <Smile className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Emoji Picker</h1>
                        <p className="text-muted-foreground">Search and copy emojis instantly</p>
                    </div>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search emojis..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Category Tabs */}
                    <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                        {recentEmojis.length > 0 && (
                            <button
                                onClick={() => setSelectedCategory('Recent')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${selectedCategory === 'Recent'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted hover:bg-muted/80'
                                    }`}
                            >
                                <Clock className="h-4 w-4" />
                                <span className="text-sm font-medium">Recent</span>
                            </button>
                        )}
                        {categories.map((category) => {
                            const Icon = CATEGORY_ICONS[category] || Smile;
                            return (
                                <button
                                    key={category}
                                    onClick={() => {
                                        setSelectedCategory(category);
                                        setSearchQuery('');
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${selectedCategory === category && !searchQuery
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted hover:bg-muted/80'
                                        }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className="text-sm font-medium">{category}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Emoji Grid */}
                    <div className="min-h-[300px]">
                        {selectedCategory === 'Recent' && recentEmojis.length > 0 ? (
                            <>
                                <p className="text-sm text-muted-foreground mb-3">Recently Used</p>
                                <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-2">
                                    {recentEmojis.map((emoji, index) => (
                                        <button
                                            key={index}
                                            onClick={() => copyEmoji(emoji)}
                                            className={`aspect-square flex items-center justify-center text-2xl rounded-lg hover:bg-muted transition-all hover:scale-110 ${copied === emoji ? 'bg-green-500/20 scale-110' : ''
                                                }`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-2">
                                {filteredEmojis.map((emoji, index) => (
                                    <button
                                        key={index}
                                        onClick={() => copyEmoji(emoji)}
                                        className={`aspect-square flex items-center justify-center text-2xl rounded-lg hover:bg-muted transition-all hover:scale-110 ${copied === emoji ? 'bg-green-500/20 scale-110' : ''
                                            }`}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Instructions */}
                    <div className="mt-6 pt-6 border-t">
                        <p className="text-sm text-muted-foreground text-center">
                            Click any emoji to copy it to clipboard
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default EmojiPicker;
